import { useMemo } from "react";

import {
  type FieldDef,
  type ResourceConfig,
  ResourcePage,
  fmtDate,
} from "@/components/resource-page";
import type { Province } from "@/utils/geo";
import type { TranslationKey } from "@/constants/i18n";
import { useTranslation } from "@/store/locale";

/**
 * What a province is made of. Exported because a city's province picker offers
 * to add one without leaving the form, and a second copy of these four fields
 * is a second place for the API's required set to drift away from.
 */
export const PROVINCE_FIELDS = (t: (key: TranslationKey) => string): FieldDef[] => [
  { name: "name", label: t("geo.name"), required: true },
  { name: "population", label: t("geo.population"), type: "number", min: 0, required: true },
  {
    name: "latitude",
    required: true,
    label: t("geo.latitude"),
    type: "number",
    step: "any",
    min: -90,
    max: 90,
  },
  {
    name: "longitude",
    required: true,
    label: t("geo.longitude"),
    type: "number",
    step: "any",
    min: -180,
    max: 180,
  },
];

export const provincesConfig = (
  t: (key: TranslationKey) => string,
): ResourceConfig<Province> => ({
  title: t("geo.provinces"),
  singular: t("geo.province"),
  plural: t("geo.provinces"),
  endpoint: "/provinces",
  model: "province",
  // The two halves of the same door: the list goes out as a CSV of the
  // columns on screen, and a CSV of those columns comes back in.
  exportable: true,
  // Written by the server, from the columns ticked in the dialog: the file
  // is every matching province, and the browser only ever holds a page of them.
  exportServer: { permission: "view__province" },
  importable: {
    columns: ["Name", "Population", "Latitude", "Longitude"],
    template: "/templates/provinces-import-sample.csv",
    file: "provinces-import-sample.csv",
    permission: "add__province",
  },
  // Deleting a place is never what anyone means: the members, clubs and
  // listings recorded against it still name it. Delete puts the row in the
  // archive, and the archive gives it back.
  archiveFilter: { name: "archived", value: "true", permission: "delete__province" },
  // Tick the rows, then one button above them: Delete on the list,
  // Restore in the archive. Behind the same right as deleting one.
  bulk: { permission: "delete__province" },
  restore: { permission: "delete__province" },
  confirmDeleteMessage: t("geo.confirmArchive"),
  columns: [
    {
      key: "id",
      label: t("rp.id"),
      sortKey: "id",
      render: (row) => (
        <span className="text-accent underline underline-offset-4">{row.id}</span>
      ),
    },
    { key: "name", label: t("geo.name"), sortKey: "name", render: (row) => row.name },
    {
      key: "population",
      label: t("geo.population"),
      sortKey: "population",
      render: (row) => row.population ?? "—",
    },
    { key: "latitude", label: t("geo.latitude"), render: (row) => row.latitude ?? "—" },
    { key: "longitude", label: t("geo.longitude"), render: (row) => row.longitude ?? "—" },
    {
      key: "created_by",
      label: t("geo.createdBy"),
      render: (row, lookup) => lookup("created_by_id", row.created_by_id),
    },
    {
      key: "created_at",
      label: t("geo.createdAt"),
      sortKey: "created_at",
      render: (row) => fmtDate(row.created_at),
    },
  ],
  defaultSort: { key: "id", order: "desc" },
  // The Employees row: a small search and a funnel on the left, the screen's
  // own actions as icons on the right.
  toolbarVariant: "compact",
  // Add, view and edit as sheets over the list, the way Employees does it:
  // these records are short, and the table behind is the context for them.
  formVariant: "modal",
  viewVariant: "modal",
  searchPlaceholder: t("rp.searchShort"),
  filters: [
    {
      name: "created_by_id",
      label: t("geo.createdByFilter"),
      type: "fk",
      endpoint: "/users/compact",
    },
    { name: "created_from", label: t("geo.createdFrom"), type: "date" },
    { name: "created_to", label: t("geo.createdTo"), type: "date" },
  ],
  fields: PROVINCE_FIELDS(t),
  detailLabels: {
    population: t("geo.population"),
    latitude: t("geo.latitude"),
    longitude: t("geo.longitude"),
  },
  toForm: (row) => ({
    name: row.name,
    population: row.population?.toString() ?? "",
    latitude: row.latitude?.toString() ?? "",
    longitude: row.longitude?.toString() ?? "",
  }),
});

function ProvincesPage() {
  const t = useTranslation();
  const config = useMemo(() => provincesConfig(t), [t]);
  return <ResourcePage config={config} />;
}

export default ProvincesPage;
